import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import {
  NodeResizeControl,
  ResizeControlVariant,
  type NodeProps,
} from '@xyflow/react';
import ContentEditable from 'react-contenteditable';
import DOMPurify from 'dompurify';

import {
  StickyNoteData,
  STICKY_NOTE_COLOR_OPTIONS,
  STICKY_NOTE_MIN_WIDTH,
  STICKY_NOTE_MIN_HEIGHT,
  DEFAULT_STICKY_NOTE_COLOR,
} from '@/types/node-types';
import { useNodeStore } from '@/stores/node-store';
import { cn } from '@/lib/utils';

import {
  StickyNoteToolbar,
  type TextFormat,
} from '@/pages/workflow/component/custom-node/sticky-note/sticky-note-toolbar';
import {
  EMPTY_LINE_HTML,
  getLineBlock,
  normalizeLineBlocks,
  collectLineBlocks,
  getLineBlocksInRange,
  applyFontSizeToLine,
  normalizeBulletList,
  getCaretOffsetInLine,
  setCaretInLine,
  setSelectionFromPoint,
  selectLineBlocks,
} from '@/pages/workflow/component/custom-node/sticky-note/sticky-note-formatting';

const isHtmlEmpty = (html: string) =>
  !html || html.replace(/<[^>]+>/g, '').trim() === '';

const STICKY_NOTE_SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'div',
    'br',
    'b',
    'strong',
    'i',
    'em',
    'u',
    's',
    'strike',
    'ul',
    'ol',
    'li',
    'a',
    'span',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
};

/** Keep line-level font styling even when the note has no visible text yet. */
const persistStickyNoteHtml = (html: string): string => {
  if (!html) return '';
  if (!isHtmlEmpty(html)) return html;
  if (/font-size\s*:/i.test(html)) return html;
  return '';
};

type StickyNoteResizeHandle =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right';

const CORNER_HANDLES = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
] as const satisfies readonly StickyNoteResizeHandle[];

const EDGE_HANDLES = [
  'top',
  'bottom',
  'left',
  'right',
] as const satisfies readonly StickyNoteResizeHandle[];

const StickyNoteNode: React.FC<NodeProps> = ({ id, data, selected }) => {
  const noteData = data as StickyNoteData;
  const text = noteData?.text ?? '';
  const backgroundColor =
    (noteData?.backgroundColor as StickyNoteData['backgroundColor']) ??
    DEFAULT_STICKY_NOTE_COLOR;
  const { bgColor, borderColor } =
    STICKY_NOTE_COLOR_OPTIONS[backgroundColor] ??
    STICKY_NOTE_COLOR_OPTIONS[DEFAULT_STICKY_NOTE_COLOR];

  const saveSnapshot = useNodeStore((state) => state.saveSnapshot);
  const updateStickyNoteData = useNodeStore(
    (state) => state.updateStickyNoteData,
  );
  const setIsTextareaHovered = useNodeStore(
    (state) => state.setIsTextareaHovered,
  );

  const handleTextChange = useCallback(
    (next: string) => updateStickyNoteData(id, { text: next }),
    [id, updateStickyNoteData],
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isLinkInputOpen, setIsLinkInputOpen] = useState(false);
  const [existingLinkUrl, setExistingLinkUrl] = useState<string | null>(null);
  const [selectionFontSize, setSelectionFontSize] = useState<string | null>(
    null,
  );
  const contentEditableRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  // Preserved while the font-size popover is open so the selection isn't lost
  // when the Radix portal renders outside the editor tree.
  const fontSizeRangeRef = useRef<Range | null>(null);
  const existingAnchorRef = useRef<HTMLAnchorElement | null>(null);
  // Coordinates of the click that started editing, so the caret can land
  // where the user clicked instead of always resetting to the first row.
  const editEntryPointRef = useRef<{ x: number; y: number } | null>(null);
  // When true, the ContentEditable onBlur does not exit edit mode (e.g. link input is open).
  const blurSuppressedRef = useRef(false);

  const resizerBounds = {
    minWidth: STICKY_NOTE_MIN_WIDTH,
    minHeight: STICKY_NOTE_MIN_HEIGHT,
  };

  const resizeHandleStyle = {
    width: 12,
    height: 12,
    backgroundColor: 'white',
    borderColor: borderColor,
    borderWidth: 2,
    borderRadius: 2,
  };

  const resizeLineStyle = {
    borderColor: borderColor,
    borderWidth: 2,
  };

  // Focus the ContentEditable when edit begins, placing the caret where the
  // user clicked if known, falling back to the first row otherwise.
  useEffect(() => {
    if (!isEditing) return;
    const entryPoint = editEntryPointRef.current;
    editEntryPointRef.current = null;
    requestAnimationFrame(() => {
      const el = contentEditableRef.current;
      if (!el) return;
      normalizeLineBlocks(el);
      // normalizeLineBlocks mutates the DOM directly; sync it back to the
      // store so the html prop matches, otherwise the next unrelated
      // re-render (e.g. selectionchange) sees a stale prop, forces a DOM
      // overwrite, and resets the caret mid-edit.
      handleTextChange(persistStickyNoteHtml(el.innerHTML));
      el.focus();

      const rangeAtPoint =
        entryPoint && setSelectionFromPoint(el, entryPoint.x, entryPoint.y);
      if (rangeAtPoint) return;

      const line = collectLineBlocks(el)[0];
      if (line) {
        setCaretInLine(line, 0);
      }
    });
  }, [isEditing, handleTextChange]);

  // Exit edit mode whenever the node loses selection. useLayoutEffect (not
  // useEffect) so this flushes before paint — otherwise the toolbar (gated
  // on `selected`) can disappear a frame before the ContentEditable does,
  // leaving it focused/editable with no toolbar in between. Removing the
  // ContentEditable can itself fire a native blur, so suppress onBlur's
  // own exit logic for a tick to avoid a second, near-identical snapshot.
  useLayoutEffect(() => {
    if (!selected && isEditing) {
      blurSuppressedRef.current = true;
      setIsEditing(false);
      setIsTextareaHovered(false);
      saveSnapshot();
      queueMicrotask(() => {
        blurSuppressedRef.current = false;
      });
    }
  }, [selected, isEditing, saveSnapshot, setIsTextareaHovered]);

  // Reset link input state when editing ends.
  useEffect(() => {
    if (!isEditing) {
      setIsLinkInputOpen(false);
      setExistingLinkUrl(null);
      blurSuppressedRef.current = false;
      savedRangeRef.current = null;
      fontSizeRangeRef.current = null;
      existingAnchorRef.current = null;
    }
  }, [isEditing]);

  // Track the computed font-size of the current selection/cursor position.
  useEffect(() => {
    if (!isEditing) {
      setSelectionFontSize(null);
      return;
    }
    const onSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        setSelectionFontSize(null);
        return;
      }
      const el = contentEditableRef.current;
      if (!el || !el.contains(selection.anchorNode)) {
        setSelectionFontSize(null);
        return;
      }
      const line = getLineBlock(selection.anchorNode, el);
      const targetEl =
        line ??
        (selection.anchorNode?.nodeType === Node.TEXT_NODE
          ? selection.anchorNode.parentElement
          : (selection.anchorNode as Element | null));
      if (!targetEl) {
        setSelectionFontSize(null);
        return;
      }
      setSelectionFontSize(window.getComputedStyle(targetEl).fontSize);
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () =>
      document.removeEventListener('selectionchange', onSelectionChange);
  }, [isEditing]);

  const handleFormat = useCallback(
    (format: TextFormat) => {
      if (!isEditing) return;
      // The toolbar's onMouseDown preventDefault keeps the ContentEditable focused,
      // so execCommand operates on the current selection.
      switch (format) {
        case 'bold':
          document.execCommand('bold');
          break;
        case 'strikethrough':
          document.execCommand('strikeThrough');
          break;
        case 'bullet': {
          document.execCommand('insertUnorderedList');
          const el = contentEditableRef.current;
          if (el) {
            // After insertUnorderedList the browser may nest former line-divs
            // inside new li elements. Flatten those and re-normalise so all
            // line blocks are correctly tracked and sized.
            normalizeBulletList(el);
            handleTextChange(el.innerHTML);
          }
          return;
        }
        case 'link': {
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            savedRangeRef.current = selection.getRangeAt(0).cloneRange();
          }
          // Show prefill + Remove only when the *entire* selection lives inside
          // a single <a> element. A mixed selection (plain + linked text) gets
          // an empty panel so the user can apply a fresh link to all of it.
          const el = contentEditableRef.current;
          const sel = window.getSelection();
          const toParentEl = (node: Node | null | undefined): Element | null =>
            !node
              ? null
              : node.nodeType === Node.TEXT_NODE
                ? node.parentElement
                : (node as Element);
          const anchorAnchor = el
            ? toParentEl(sel?.anchorNode)?.closest<HTMLAnchorElement>('a')
            : null;
          const focusAnchor = el
            ? toParentEl(sel?.focusNode)?.closest<HTMLAnchorElement>('a')
            : null;
          // Both endpoints must resolve to the same <a> within the editor.
          const existingAnchor =
            anchorAnchor &&
            focusAnchor &&
            anchorAnchor === focusAnchor &&
            el?.contains(anchorAnchor)
              ? anchorAnchor
              : null;
          existingAnchorRef.current = existingAnchor;
          setExistingLinkUrl(
            existingAnchor ? (existingAnchor.getAttribute('href') ?? '') : null,
          );
          blurSuppressedRef.current = true;
          setIsLinkInputOpen(true);
          return; // Link is applied asynchronously via handleLinkApply.
        }
      }
      // Sync the updated innerHTML back to the store.
      const el = contentEditableRef.current;
      if (el) handleTextChange(el.innerHTML);
    },
    [isEditing, handleTextChange],
  );

  const handleLinkApply = useCallback(
    (url: string) => {
      blurSuppressedRef.current = false;
      setIsLinkInputOpen(false);

      if (!url || !savedRangeRef.current) {
        savedRangeRef.current = null;
        existingAnchorRef.current = null;
        return;
      }

      const el = contentEditableRef.current;
      if (!el) {
        savedRangeRef.current = null;
        existingAnchorRef.current = null;
        return;
      }

      el.focus();

      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedRangeRef.current);
      }
      savedRangeRef.current = null;

      // Leave URLs that already specify a scheme (mailto:, tel:, https:, ...),
      // a site-relative path, or a fragment alone; only bare domains like
      // "example.com" need a scheme added.
      const hasSchemeOrPath = /^([a-z][a-z0-9+.-]*:|\/|#)/i.test(url);
      const formattedUrl = hasSchemeOrPath ? url : `https://${url}`;
      document.execCommand('createLink', false, formattedUrl);
      existingAnchorRef.current = null;
      handleTextChange(el.innerHTML);
    },
    [handleTextChange],
  );

  const handleLinkCancel = useCallback(() => {
    blurSuppressedRef.current = false;
    setIsLinkInputOpen(false);
    setExistingLinkUrl(null);
    savedRangeRef.current = null;
    existingAnchorRef.current = null;
    contentEditableRef.current?.focus();
  }, []);

  const handleLinkRemove = useCallback(() => {
    blurSuppressedRef.current = false;
    setIsLinkInputOpen(false);
    setExistingLinkUrl(null);

    const el = contentEditableRef.current;
    const anchor = existingAnchorRef.current;
    existingAnchorRef.current = null;
    savedRangeRef.current = null;
    if (!el || !anchor) return;

    el.focus();

    // Select the whole <a> — not just the saved (possibly collapsed) caret
    // range — so unlink acts on the entire link instead of no-op'ing.
    const selection = window.getSelection();
    if (selection) {
      const range = document.createRange();
      range.selectNode(anchor);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    document.execCommand('unlink');
    handleTextChange(el.innerHTML);
  }, [handleTextChange]);

  // Called when the font-size popover opens, to preserve the current selection
  // before the Radix portal potentially causes focus/selection loss.
  const handleSaveFontSizeSelection = useCallback(() => {
    const el = contentEditableRef.current;
    const selection = window.getSelection();
    if (!el || !selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) return;
    fontSizeRangeRef.current = range.cloneRange();
  }, []);

  const handleFontSize = useCallback(
    (size: string, lineHeight: string) => {
      if (!isEditing) return;

      const el = contentEditableRef.current;
      if (!el) return;

      // Restore the selection saved before the popover opened, in case opening
      // the Radix portal collapsed or moved the browser selection. Guarded
      // against a stale range (DOM changed since it was captured) throwing
      // out of addRange.
      const savedRange = fontSizeRangeRef.current;
      fontSizeRangeRef.current = null;
      if (
        savedRange &&
        el.contains(savedRange.startContainer) &&
        el.contains(savedRange.endContainer)
      ) {
        el.focus();
        try {
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(savedRange);
        } catch {
          // Saved range is no longer valid — fall through to whatever
          // selection currently exists.
        }
      }

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      try {
        const range = selection.getRangeAt(0);
        const collapsed = range.collapsed;
        const caretLine = collapsed
          ? getLineBlock(range.startContainer, el)
          : null;
        const caretOffset =
          collapsed && caretLine ? getCaretOffsetInLine(caretLine, range) : 0;

        // Use Range.intersectsNode to determine affected lines — this correctly
        // handles Ctrl+A selections where startContainer/endContainer are the
        // root element itself (container-walking would only find the first line).
        const lines = getLineBlocksInRange(el, range);
        if (lines.length === 0) return;

        lines.forEach((line) => applyFontSizeToLine(line, size, lineHeight));

        const activeLine = caretLine ?? lines[0];
        if (collapsed && activeLine) {
          setCaretInLine(activeLine, caretOffset);
        } else {
          selectLineBlocks(lines, selection);
        }
      } catch {
        // Selection crossed non-splittable boundaries — skip silently.
      }

      handleTextChange(persistStickyNoteHtml(el.innerHTML));
    },
    [isEditing, handleTextChange],
  );

  const renderResizeControl = (
    position: StickyNoteResizeHandle,
    variant: ResizeControlVariant,
    style: CSSProperties,
  ) => (
    <NodeResizeControl
      key={position}
      position={position}
      variant={variant}
      {...resizerBounds}
      style={style}
    />
  );

  return (
    <>
      {selected && (
        <>
          {EDGE_HANDLES.map((position) =>
            renderResizeControl(
              position,
              ResizeControlVariant.Line,
              resizeLineStyle,
            ),
          )}
          {CORNER_HANDLES.map((position) =>
            renderResizeControl(
              position,
              ResizeControlVariant.Handle,
              resizeHandleStyle,
            ),
          )}
        </>
      )}
      {selected && (
        <StickyNoteToolbar
          nodeId={id}
          data={noteData}
          isEditing={isEditing}
          onFormat={handleFormat}
          onFontSize={handleFontSize}
          onSaveFontSizeSelection={handleSaveFontSizeSelection}
          selectionFontSize={selectionFontSize}
          isLinkInputOpen={isLinkInputOpen}
          existingLinkUrl={existingLinkUrl}
          onLinkApply={handleLinkApply}
          onLinkCancel={handleLinkCancel}
          onLinkRemove={handleLinkRemove}
        />
      )}
      <div
        data-testid='sticky_note_node'
        className={cn(
          'flex h-full w-full flex-col rounded-sm border shadow-md overflow-hidden',
          !isEditing && !selected ? 'cursor-grab' : 'cursor-default',
        )}
        style={{
          backgroundColor: bgColor,
          borderColor: borderColor,
          borderWidth: selected ? 2 : 1,
        }}
      >
        {/*
         * When editing: nodrag lets the user select text without moving the node.
         * When not editing: no nodrag so the entire note surface is draggable.
         * nowheel always prevents scroll-zoom conflicts inside the note.
         */}
        <div
          className={cn(
            'nowheel flex min-h-0 flex-1 flex-col overflow-hidden p-2.5',
            isEditing && 'nodrag cursor-default',
          )}
        >
          {isEditing ? (
            <ContentEditable
              innerRef={contentEditableRef as RefObject<HTMLElement>}
              html={DOMPurify.sanitize(
                text || EMPTY_LINE_HTML,
                STICKY_NOTE_SANITIZE_CONFIG,
              )}
              disabled={false}
              onChange={(e) => {
                // '<br>' is what the browser reports for an empty contenteditable
                handleTextChange(persistStickyNoteHtml(e.target.value));
              }}
              tagName='div'
              onBlur={() => {
                if (blurSuppressedRef.current) return;
                setIsEditing(false);
                setIsTextareaHovered(false);
                saveSnapshot();
              }}
              onFocus={() => setIsTextareaHovered(true)}
              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                e.stopPropagation();
                // Let the blur handler own the single exit-edit-mode path.
                if (e.key === 'Escape') e.currentTarget.blur();
              }}
              onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
                e.stopPropagation()
              }
              onMouseEnter={() => setIsTextareaHovered(true)}
              onMouseLeave={() => setIsTextareaHovered(false)}
              className='nodrag sticky-note-rich w-full cursor-text max-h-full overflow-y-auto outline-none text-xs leading-4 text-black'
            />
          ) : (
            <div
              role='button'
              tabIndex={selected ? 0 : -1}
              className={cn(
                'w-full max-h-full overflow-y-auto text-xs leading-4 text-black select-none',
                // Override role='button''s default pointer cursor to preserve
                // the draggable affordance when not in focus/edit mode.
                !selected && 'cursor-grab',
              )}
              onClick={(e) => {
                // Let links in the rendered content navigate instead of
                // also entering edit mode.
                if ((e.target as HTMLElement).closest('a')) return;
                // D3-drag suppresses click events after any drag, so this only fires on
                // genuine clicks. Check selected to distinguish first-click (selects node)
                // from second-click (enter edit).
                if (!selected) return;
                editEntryPointRef.current = { x: e.clientX, y: e.clientY };
                setIsEditing(true);
              }}
              onKeyDown={(e) => {
                if (!selected) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsEditing(true);
                }
              }}
              onMouseEnter={() => setIsTextareaHovered(true)}
              onMouseLeave={() => setIsTextareaHovered(false)}
            >
              {isHtmlEmpty(text) ? (
                <span className={`text-black/40 ${selected && 'cursor-text'}`}>
                  Click to edit…
                </span>
              ) : (
                <div
                  className={`sticky-note-rich ${selected && 'cursor-text'}`}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      text,
                      STICKY_NOTE_SANITIZE_CONFIG,
                    ),
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default memo(StickyNoteNode);
